/**
 * RaceFinishNotification — transient toast popup for race finishes.
 *
 * Shows a brief notification when a player finishes the race, displaying
 * their place and finish time. Auto-clears after 8s (handled by
 * GmEventService).
 *
 * @see docs/design/gm/GM_EVENT_PHASE4_SPEC.md §4.2
 */

import React from 'react';
import { useGmEvent } from '../../../services/GmEventService';
import { COLORS, FONTS } from '../../theme';

function getLocalSessionId(): string | null {
  return (window as any).nm?.getRoom?.()?.sessionId ?? null;
}

function formatTime(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const tenths = Math.floor((ms % 1000) / 100);
  return mins > 0
    ? `${mins}:${secs.toString().padStart(2, '0')}.${tenths}`
    : `${secs}.${tenths}s`;
}

const PLACE_LABELS: Record<number, string> = {
  1: '1st',
  2: '2nd',
  3: '3rd',
};

export const RaceFinishNotification: React.FC = () => {
  const { raceFinish } = useGmEvent();

  if (!raceFinish) return null;

  const localSid = getLocalSessionId();
  const isMe = raceFinish.sessionId === localSid;
  const placeLabel = PLACE_LABELS[raceFinish.place] ?? `${raceFinish.place}th`;
  const placeColor = raceFinish.place === 1 ? COLORS.textAccent
    : raceFinish.place === 2 ? COLORS.textSecondary
    : raceFinish.place === 3 ? '#cd7f32'
    : COLORS.textMuted;

  const containerStyle: React.CSSProperties = {
    position: 'absolute',
    top: 80,
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '10px 20px',
    background: COLORS.surface,
    border: `1px solid ${placeColor}`,
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    fontFamily: FONTS.ui,
    zIndex: 100,
    pointerEvents: 'none',
  };

  return (
    <div style={containerStyle}>
      <div style={{ fontSize: 16, fontWeight: 'bold', color: placeColor }}>
        {isMe ? 'You finished!' : 'Player finished!'}
      </div>
      <div style={{ fontSize: 14, color: COLORS.textPrimary }}>
        {placeLabel} place — {formatTime(raceFinish.finishTimeMs)}
      </div>
    </div>
  );
};
